/**
 * What the import screens decide, as functions. `renderToStaticMarkup` fires no event and runs
 * no effect, so everything a screen derives from an import's row lives here and is called
 * directly by `test/u78-*`.
 */
import {
  PLANS,
  TIERS,
  importCheckpointSchema,
  importMappingSchema,
  importStatsSchema,
  type ImportCheckpoint,
  type ImportDateOrder,
  type ImportDetail,
  type ImportMapping,
  type ImportStats,
  type ImportStatus,
  type Tier,
} from "@packagepage/shared";

/** The tier that opens the screen (Q-P23). A trial organisation reads `growth` and is above it. */
export const IMPORT_TIER = "starter" as const satisfies Tier;

export const IMPORT_POLL_MS = 2000;

/**
 * A status the worker moves on its own. `mapping` and `review` wait for a person, and the
 * three after them are over. Total, so a tenth status is a compile error here.
 */
export const POLLED: Readonly<Record<ImportStatus, boolean>> = {
  uploaded: true,
  parsing: true,
  mapping: false,
  validating: true,
  review: false,
  applying: true,
  done: false,
  failed: false,
  cancelled: false,
};

/** `refetchInterval` for the detail query: false until the first answer and after every status that is not polled. */
export function importPollInterval(status: ImportStatus | undefined): number | false {
  return status !== undefined && POLLED[status] ? IMPORT_POLL_MS : false;
}

/** Where `cancelImport` accepts the call. `applying` is refused by the door; the rest are over. */
export const CANCELLABLE: Readonly<Record<ImportStatus, boolean>> = {
  uploaded: true,
  parsing: true,
  mapping: true,
  validating: true,
  review: true,
  applying: false,
  done: false,
  failed: false,
  cancelled: false,
};

export const STEP_LABELS = [
  "Upload",
  "Match columns",
  "Check",
  "Review",
  "Apply",
] as const;

/** The stepper's current step, or null when the import ended somewhere other than the end. */
export function stepOf(status: ImportStatus): {
  current: number | null;
  complete: boolean;
} {
  switch (status) {
    case "uploaded":
    case "parsing":
      return { current: 0, complete: false };
    case "mapping":
      return { current: 1, complete: false };
    case "validating":
      return { current: 2, complete: false };
    case "review":
      return { current: 3, complete: false };
    case "applying":
      return { current: 4, complete: false };
    case "done":
      return { current: null, complete: true };
    case "failed":
    case "cancelled":
      return { current: null, complete: false };
  }
}

export const STATUS_WORDS: Readonly<Record<ImportStatus, string>> = {
  uploaded: "Uploading",
  parsing: "Reading the file",
  mapping: "Waiting for your columns",
  validating: "Checking",
  review: "Ready to review",
  applying: "Applying",
  done: "Done",
  failed: "Stopped",
  cancelled: "Cancelled",
};

export function readStats(stats: unknown): ImportStats {
  const parsed = importStatsSchema.safeParse(stats ?? {});
  return parsed.success ? parsed.data : {};
}

export function readCheckpoint(checkpoint: unknown): ImportCheckpoint {
  const parsed = importCheckpointSchema.safeParse(checkpoint ?? {});
  return parsed.success ? parsed.data : {};
}

export function readMapping(mapping: unknown): ImportMapping | null {
  const parsed = importMappingSchema.safeParse(mapping);
  return parsed.success ? parsed.data : null;
}

const WHOLE = new Intl.NumberFormat("en-GB");
export const count = (n: number): string => WHOLE.format(n);

export interface Progress {
  readonly done: number;
  readonly total: number;
  /** 0 to 1. 0 when the total is not known yet. */
  readonly fraction: number;
}

function progress(done: number, total: number): Progress {
  if (total <= 0) return { done, total, fraction: 0 };
  return { done, total, fraction: Math.min(1, done / total) };
}

/**
 * State C. The numerator is `validatedRowNo`: parse leaves `rowNo` at the file's last row, so a
 * bar on it reads 100% from the first poll (Drift §U75).
 */
export function checkingProgress(
  detail: Pick<ImportDetail, "stats" | "checkpoint">,
): Progress {
  const stats = readStats(detail.stats);
  const checkpoint = readCheckpoint(detail.checkpoint);
  return progress(checkpoint.validatedRowNo ?? 0, stats.rows ?? 0);
}

export interface ApplyProgress extends Progress {
  readonly failed: number;
}

/**
 * State F. Numerator and denominator are both counts: `stats.applied` over the creates the
 * limit admits plus every update. `checkpoint.applied` is a row_no watermark and is never read
 * here (Drift §U77).
 */
export function applyingProgress(
  detail: Pick<ImportDetail, "stats" | "checkpoint">,
): ApplyProgress {
  const stats = readStats(detail.stats);
  const limit = readCheckpoint(detail.checkpoint).apply?.limit ?? null;
  const creates = stats.create ?? 0;
  const total =
    (stats.update ?? 0) + (limit === null ? creates : Math.min(limit, creates));
  return { ...progress(stats.applied ?? 0, total), failed: stats.applyFailed ?? 0 };
}

export interface DoneFigures {
  readonly applied: number;
  readonly failed: number;
  readonly notApplied: number;
  /** Null when `stats` holds no split: an import applied before apply recorded one. */
  readonly created: number | null;
  readonly updated: number | null;
  /** Rows validation refused: the error CSV's length. */
  readonly toFix: number;
}

/**
 * State G. Every figure here is one apply counted, except `notApplied` for an import whose
 * `stats` predates the key, where `create + update − applied − failed` is the best available.
 */
export function doneFigures(detail: Pick<ImportDetail, "stats">): DoneFigures {
  const stats = readStats(detail.stats);
  const applied = stats.applied ?? 0;
  const failed = stats.applyFailed ?? 0;
  const wanted = (stats.create ?? 0) + (stats.update ?? 0);
  return {
    applied,
    failed,
    notApplied: stats.notApplied ?? Math.max(0, wanted - applied - failed),
    created: stats.created ?? null,
    updated: stats.updated ?? null,
    toFix: stats.errors ?? 0,
  };
}

export interface ReviewFigures {
  readonly rows: number;
  readonly errors: number;
  readonly create: number;
  readonly update: number;
  readonly skip: number;
  /** Rows an apply would write: creates plus updates. */
  readonly ready: number;
  /** Share of the file that is ready, to one decimal place. */
  readonly readyPercent: number;
}

export function reviewFigures(detail: Pick<ImportDetail, "stats">): ReviewFigures {
  const stats = readStats(detail.stats);
  const rows = stats.rows ?? 0;
  const create = stats.create ?? 0;
  const update = stats.update ?? 0;
  const ready = create + update;
  return {
    rows,
    errors: stats.errors ?? 0,
    create,
    update,
    skip: stats.skip ?? 0,
    ready,
    readyPercent: rows === 0 ? 0 : Math.round((ready / rows) * 1000) / 10,
  };
}

export interface OverLimitFigures {
  readonly allowance: number;
  readonly published: number;
  readonly newProducts: number;
  readonly shortBy: number;
  readonly slotsLeft: number;
  /** The creates `Apply N now` admits; null when no slot is left to apply into. */
  readonly applyNow: number | null;
  /** Rows the file keeps for later when the subset is applied. */
  readonly remaining: number;
}

export function overLimitFigures(
  overLimit: NonNullable<ImportDetail["overLimit"]>,
): OverLimitFigures {
  const slotsLeft = overLimit.allowance - overLimit.published;
  const applyNow = slotsLeft > 0 ? Math.min(slotsLeft, overLimit.newProducts) : null;
  return {
    ...overLimit,
    slotsLeft,
    applyNow,
    remaining:
      applyNow === null ? overLimit.newProducts : overLimit.newProducts - applyNow,
  };
}

export interface UpgradeTarget {
  readonly tier: Tier;
  readonly monthlyUsd: number;
  readonly activeGtins: number;
}

/**
 * The next plan above `plan` that has a list price. `plans` is injectable so a test can give
 * it a table whose figures no source file contains.
 */
export function upgradeTarget(
  plan: Tier,
  plans: Readonly<
    Record<Tier, { monthlyUsd: number | null; activeGtins: number }>
  > = PLANS,
): UpgradeTarget | null {
  for (const tier of TIERS.slice(TIERS.indexOf(plan) + 1)) {
    const row = plans[tier];
    if (row.monthlyUsd !== null) {
      return { tier, monthlyUsd: row.monthlyUsd, activeGtins: row.activeGtins };
    }
  }
  return null;
}

export const DATE_ORDER_LABELS: Readonly<Record<ImportDateOrder, string>> = {
  dmy: "Day first — 31/12/2027",
  mdy: "Month first — 12/31/2027",
  ymd: "Year first — 2027-12-31",
};

const DATE_FIELDS: readonly string[] = ["batch.expiry", "batch.mfgDate"];

export const mapsADate = (fields: readonly (string | null)[]): boolean =>
  fields.some((f) => f !== null && DATE_FIELDS.includes(f));

/** The control's starting value: what the import already holds, else what its template saved. */
export function seedDateOrder(
  mapping: ImportMapping | null,
  template: ImportDetail["template"],
): ImportDateOrder | null {
  return mapping?.dateOrder ?? template?.dateOrder ?? null;
}

export interface MappingFaults {
  /** What the door would refuse, said before the request. */
  readonly blocking: string | null;
  /** What the door allows and the review will repeat on every row. */
  readonly notes: readonly string[];
}

export function mappingFaults(fields: readonly (string | null)[]): MappingFaults {
  const chosen = fields.filter((f): f is string => f !== null);
  let blocking: string | null = null;
  const twice = chosen.find((f, i) => chosen.indexOf(f) !== i);
  if (!chosen.includes("gtin14")) {
    blocking =
      "No column is matched to the GTIN. Choose the column that holds each product's GTIN.";
  } else if (twice !== undefined) {
    blocking = "Two columns are matched to the same field. Leave one of them unmatched.";
  } else if (mapsADate(chosen) && !chosen.includes("batch.lot")) {
    blocking =
      "A batch date needs a batch lot to belong to. Match a lot column, or leave the date unmatched.";
  }
  const notes: string[] = [];
  if (!chosen.includes("brandName")) {
    notes.push(
      "No column is matched to Brand. Every row that would create a new product needs one, and each of those rows will be refused with the same sentence. Rows that only update existing products are not affected.",
    );
  }
  return { blocking, notes };
}

export function percent(fraction: number): string {
  return `${String(Math.round(fraction * 100))}%`;
}
